export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export class AppRadioGroup extends HTMLElement {
  private _options: RadioOption[] = [];

  static get observedAttributes() {
    return ["label", "name", "value", "disabled", "required", "direction", "variant"];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  set options(opts: RadioOption[]) {
    this._options = opts;
    this.render();
  }

  get options(): RadioOption[] {
    return this._options;
  }

  get value(): string {
    const checked = this.querySelector<HTMLInputElement>("input[type='radio']:checked");
    return checked ? checked.value : "";
  }

  set value(val: string) {
    const radio = this.querySelector<HTMLInputElement>(`input[type='radio'][value='${val}']`);
    if (radio) {
      radio.checked = true;
    }
  }

  private render() {
    const label = this.getAttribute("label") || "";
    const name = this.getAttribute("name") || `radio-${Math.random().toString(36).substring(2, 9)}`;
    const currentValue = this.getAttribute("value") || "";
    const disabled = this.hasAttribute("disabled");
    const required = this.hasAttribute("required");
    const direction = this.getAttribute("direction") || "row";
    const variant = this.getAttribute("variant") || "default"; // 'default' or 'technical-decision'

    // If technical decision variant and no options provided, provide default Aprovado/Reprovado/N.A.
    if (variant === "technical-decision" && this._options.length === 0) {
      this._options = [
        { value: "APPROVED", label: "Aprovado" },
        { value: "REJECTED", label: "Reprovado" },
        { value: "NOT_APPLICABLE", label: "Não Aplicável" }
      ];
    }

    const optionsHtml = this._options.map(opt => {
      const id = `${name}-${opt.value}`;
      return `
        <label for="${id}" style="display: inline-flex; align-items: center; gap: 6px; font-size: 0.8125rem; cursor: ${disabled || opt.disabled ? "not-allowed" : "pointer"}; user-select: none;">
          <input
            id="${id}"
            type="radio"
            name="${name}"
            value="${opt.value}"
            ${opt.value === currentValue ? "checked" : ""}
            ${disabled || opt.disabled ? "disabled" : ""}
            ${required ? "required" : ""}
            style="accent-color: var(--color-primary); width: 15px; height: 15px;"
          />
          <span>${opt.label}</span>
        </label>
      `;
    }).join("");

    this.innerHTML = `
      <div class="form-group">
        ${label ? `<span class="form-label ${required ? "required" : ""}">${label}</span>` : ""}
        <div style="display: flex; flex-direction: ${direction === "column" ? "column" : "row"}; gap: 16px; flex-wrap: wrap; margin-top: 4px;">
          ${optionsHtml}
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-radio-group")) {
  if (!customElements.get("app-radio-group")) {
    customElements.define("app-radio-group", AppRadioGroup);
  }
}
